import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutDashboard, Sparkles, Calendar, RefreshCw } from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { AuthContext } from '../../context/AuthContext';
import AIVoiceAgentPanel from './ai-caller/AIVoiceAgentPanel';
import {
  AlertsPanel, EnquiryFunnelPanel, TeamWorkloadPanel, ReferralSourcesPanel,
  HotPatientsPanel, EnquiryTrendPanel, RevenueProjectionPanel,
} from './DashboardPanels';


const KPI_CARDS = [
  { key: 'new_today',              label: 'New today',            color: 'text-blue-600 dark:text-blue-400',    href: '/engage/enquiries?date=today' },
  { key: 'new_this_week',          label: 'New this week',        color: 'text-cyan-600 dark:text-cyan-400',    href: '/engage/enquiries?date=last7days' },
  { key: 'new_this_month',         label: 'New this month',       color: 'text-purple-600 dark:text-purple-400', href: '/engage/enquiries?date=last30days' },
  { key: 'appointment_booked',     label: 'Appointments booked',  color: 'text-teal-600 dark:text-teal-400',    href: '/engage/enquiries?status=appointment_booked' },
  { key: 'follow_up_required',     label: 'Follow-up required',   color: 'text-amber-600 dark:text-amber-400',  href: '/engage/enquiries?status=follow_up_required' },
  { key: 'converted_this_month',   label: 'Converted (30d)',      color: 'text-green-600 dark:text-green-400',  href: '/engage/enquiries?status=converted&date=last30days' },
  { key: 'dropped_this_month',     label: 'Dropped (30d)',        color: 'text-red-600 dark:text-red-400',      href: '/engage/enquiries?status=dropped&date=last30days' },
  { key: 'conversion_rate_pct',    label: 'Conversion %',         color: 'text-indigo-600 dark:text-indigo-400', suffix: '%', href: '/engage/reports/enquiry-funnel' },
];


// ── Period preset resolver ─────────────────────────────────────────────────

const iso = (d) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const PERIOD_PRESETS = [
  { value: 'today',       label: 'Today' },
  { value: 'yesterday',   label: 'Yesterday' },
  { value: 'this_week',   label: 'This week' },
  { value: 'last_week',   label: 'Last week' },
  { value: 'this_month',  label: 'This month' },
  { value: 'last_month',  label: 'Last month' },
  { value: 'last_7',      label: 'Last 7 days' },
  { value: 'last_30',     label: 'Last 30 days' },
  { value: 'last_90',     label: 'Last 90 days' },
  { value: 'this_year',   label: 'This year' },
  { value: 'custom',      label: 'Custom' },
];

function presetToRange(preset, customFrom, customTo) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const back = (days) => new Date(today.getFullYear(), today.getMonth(), today.getDate() - days);

  switch (preset) {
    case 'today':      return { from: iso(today), to: iso(today) };
    case 'yesterday':  { const y = back(1); return { from: iso(y), to: iso(y) }; }
    case 'this_week':  {
      const dow = today.getDay(); // Sun=0
      const monday = back(dow === 0 ? 6 : dow - 1);
      return { from: iso(monday), to: iso(today) };
    }
    case 'last_week':  {
      const dow = today.getDay();
      const thisMon = back(dow === 0 ? 6 : dow - 1);
      const lastSun = new Date(thisMon); lastSun.setDate(thisMon.getDate() - 1);
      const lastMon = new Date(thisMon); lastMon.setDate(thisMon.getDate() - 7);
      return { from: iso(lastMon), to: iso(lastSun) };
    }
    case 'this_month': {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: iso(first), to: iso(today) };
    }
    case 'last_month': {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last  = new Date(today.getFullYear(), today.getMonth(),     0);
      return { from: iso(first), to: iso(last) };
    }
    case 'last_7':     return { from: iso(back(6)),  to: iso(today) };
    case 'last_30':    return { from: iso(back(29)), to: iso(today) };
    case 'last_90':    return { from: iso(back(89)), to: iso(today) };
    case 'this_year':  {
      const jan1 = new Date(today.getFullYear(), 0, 1);
      return { from: iso(jan1), to: iso(today) };
    }
    case 'custom':     return { from: customFrom || null, to: customTo || null };
    default:           return { from: null, to: null };
  }
}


export default function EngageDashboard() {
  const { fetch: apiFetch } = useApiMethods();
  const { hasFeature } = useContext(AuthContext);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [preset, setPreset] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [reloadTick, setReloadTick] = useState(0);
  const [arpp, setArpp] = useState(15000);   // avg realised revenue per converted patient

  const { from, to } = useMemo(() => presetToRange(preset, customFrom, customTo), [preset, customFrom, customTo]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const q = new URLSearchParams();
        if (from) q.set('from', from);
        if (to)   q.set('to',   to);
        if (arpp) q.set('arpp', String(arpp));
        const resp = await apiFetch(`engage/analytics/dashboard-summary/?${q.toString()}`);
        if (!cancelled) setSummary(resp?.data || resp?.data?.data || null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, arpp, reloadTick]);

  const periodKpi = summary?.period_kpi;

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white shadow-sm">
            <LayoutDashboard className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Hospital CRM — Dashboard</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Patient enquiry pipeline across all inflow channels
              {periodKpi && (
                <span> · showing <span className="font-medium text-gray-700 dark:text-gray-300">{PERIOD_PRESETS.find(p => p.value === preset)?.label || 'period'}</span>
                  {' '}({periodKpi.range_from} → {periodKpi.range_to})</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Period picker */}
          <div className="inline-flex items-center gap-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-1.5">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            <select value={preset} onChange={e => setPreset(e.target.value)}
              className="text-sm bg-transparent text-gray-900 dark:text-white focus:outline-none">
              {PERIOD_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          {preset === 'custom' && (
            <>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                className="px-2 py-1.5 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white" />
              <span className="text-xs text-gray-500">→</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                className="px-2 py-1.5 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white" />
            </>
          )}
          <button onClick={() => setReloadTick(t => t + 1)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-xs font-medium">
            <Sparkles className="w-3.5 h-3.5" /> Engage v2
          </span>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Period KPIs — reflect the chosen range */}
      {periodKpi && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Link to="/engage/enquiries"
            className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-4 border border-blue-100 dark:border-blue-800/60 shadow-sm hover:shadow-md transition-shadow"
            title="Open enquiries list">
            <div className="text-[10px] uppercase tracking-wide text-blue-700 dark:text-blue-300 font-medium">Enquiries in period</div>
            <div className="text-2xl font-bold mt-1 text-blue-700 dark:text-blue-300">{loading ? '…' : periodKpi.new}</div>
            <div className="text-[10px] text-blue-600/70 mt-0.5">{periodKpi.range_days} day{periodKpi.range_days !== 1 ? 's' : ''}</div>
          </Link>
          <Link to="/engage/enquiries?status=converted"
            className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-4 border border-green-100 dark:border-green-800/60 shadow-sm hover:shadow-md transition-shadow"
            title="Open converted enquiries">
            <div className="text-[10px] uppercase tracking-wide text-green-700 dark:text-green-300 font-medium">Converted</div>
            <div className="text-2xl font-bold mt-1 text-green-700 dark:text-green-300">{loading ? '…' : periodKpi.converted}</div>
            <div className="text-[10px] text-green-600/70 mt-0.5">demo → registered patient</div>
          </Link>
          <Link to="/engage/enquiries?status=dropped"
            className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 rounded-xl p-4 border border-red-100 dark:border-red-800/60 shadow-sm hover:shadow-md transition-shadow"
            title="Open dropped enquiries">
            <div className="text-[10px] uppercase tracking-wide text-red-700 dark:text-red-300 font-medium">Dropped / lost</div>
            <div className="text-2xl font-bold mt-1 text-red-700 dark:text-red-300">{loading ? '…' : periodKpi.dropped}</div>
            <div className="text-[10px] text-red-600/70 mt-0.5">didn't convert this period</div>
          </Link>
          <Link to="/engage/reports/enquiry-funnel"
            className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-4 border border-indigo-100 dark:border-indigo-800/60 shadow-sm hover:shadow-md transition-shadow"
            title="Open the enquiry funnel report">
            <div className="text-[10px] uppercase tracking-wide text-indigo-700 dark:text-indigo-300 font-medium">Conversion rate</div>
            <div className="text-2xl font-bold mt-1 text-indigo-700 dark:text-indigo-300">{loading ? '…' : `${periodKpi.conversion_rate_pct}%`}</div>
            <div className="text-[10px] text-indigo-600/70 mt-0.5">converted / new</div>
          </Link>
        </div>
      )}

      {/* Always-current KPI strip (today/week/month baselines) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {KPI_CARDS.map(card => {
          const val = summary?.kpi?.[card.key];
          const display = loading ? '…' : (val ?? 0);
          return (
            <Link
              key={card.key}
              to={card.href || '/engage/enquiries'}
              className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md transition-all"
              title={`Drill into ${card.label}`}
            >
              <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">{card.label}</div>
              <div className={`text-xl font-bold mt-1 ${card.color}`}>
                {display}{card.suffix || ''}
              </div>
            </Link>
          );
        })}
      </div>

      {/* Alerts — aging + overdue follow-ups (always current) */}
      <AlertsPanel alerts={summary?.alerts} />

      {/* Trend chart — scoped to picked period */}
      <EnquiryTrendPanel trend={summary?.trend_30d} />

      {/* Funnel + Team workload */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <EnquiryFunnelPanel funnel={summary?.funnel} />
        <TeamWorkloadPanel team={summary?.team_workload} />
      </div>

      {/* Hot patients */}
      <HotPatientsPanel patients={summary?.hot_patients} />

      {/* Enquiries by source */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Enquiries by source{periodKpi ? ` · ${PERIOD_PRESETS.find(p => p.value === preset)?.label || 'period'}` : ''}
        </h2>
        {loading ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>
        ) : summary?.by_source?.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {summary.by_source.map(row => (
              <Link
                key={row.source}
                to={`/engage/enquiries?source=${row.source}`}
                className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-sm transition-all"
                title={`Click to see all ${row.count} ${(row.source || '').replace(/_/g, ' ')} enquiries`}
              >
                <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">
                  {(row.source || 'unknown').replace(/_/g, ' ')}
                </div>
                <div className="text-lg font-bold mt-1 text-gray-900 dark:text-white">{row.count}</div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400">No enquiries in the selected period.</div>
        )}
      </div>

      {/* Referral sources overview */}
      <ReferralSourcesPanel referralSources={summary?.referral_sources} />

      {/* Source-wise revenue projection */}
      <RevenueProjectionPanel
        projection={summary?.revenue_projection}
        arppOverride={arpp}
        onArppChange={setArpp}
        onOverridesSaved={() => setReloadTick(t => t + 1)}
        rangeFrom={from}
        rangeTo={to}
      />

      {/* AI Voice Agent analytics — only when the plan enables AI Caller */}
      {hasFeature('engage_ai_caller') && <AIVoiceAgentPanel />}

      {/* Recent activity */}
      {summary?.recent_activity?.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Recent activity</h2>
          <div className="space-y-2">
            {summary.recent_activity.map(item => (
              <div key={item.id} className="flex items-center gap-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium w-20">
                  {item.log_type}
                </div>
                <Link to={`/engage/enquiries/${item.enquiry_id}`} className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium">
                  {item.enquiry_name || `Enquiry #${item.enquiry_id}`}
                </Link>
                <span className="text-xs text-gray-600 dark:text-gray-300 flex-1 truncate">
                  {item.message}
                </span>
                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                  {new Date(item.created_date).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
